/**
 * Zod schemas for stage-permission admin (Issue #1559 — BIC Stream D / Phase 5b).
 *
 * Validates upsert input from the per-tenant stage-permission editor before
 * the value reaches the 13-workflow service. The schema mirrors
 * `UpsertStagePermissionInput` from the DTO module.
 *
 * @see ./../dtos/stage-permission.dto
 */

import { z } from 'zod';
import { UserRole } from '../enums/roles';

const userRoleEnum = z.nativeEnum(UserRole);

/**
 * Schema for `UpsertStagePermissionInput`. Decimal-string is enforced via
 * regex (avoids float drift on the wire) and capped to 2 fraction digits
 * (USD cents). `restrictedToTeams` validates UUID shape but doesn't check
 * existence — the service layer rejects unknown ids.
 */
export const upsertStagePermissionInputSchema = z.object({
  businessId: z.string().uuid(),
  pipelineId: z.string().uuid(),
  stageId: z.string().uuid(),
  allowedRoles: z.array(userRoleEnum).default([]),
  requiredLicenses: z.array(z.string()).default([]),
  maxAmountSingleApproval: z
    .string()
    .regex(/^-?\d+(\.\d{1,2})?$/, 'must be decimal with up to 2 fraction digits')
    .optional(),
  requiresDualApproval: z.boolean().default(false),
  approverRoles: z.array(userRoleEnum).default([]),
  minApproversRequired: z.number().int().min(1).max(10).default(2),
  restrictedToTeams: z.array(z.string().uuid()).default([]),
  isActive: z.boolean().default(true),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
